<script>
/**
 * Type         : Modal
 * Title        : 배송상품 등록 - 엑셀 대량등록 
 * Description  : 신규 배송 상품 등록을 엑셀을 통해 대량등록을 하는 모달 (Stepper UI 적용)
 * 
 */
import * as XLSX from 'xlsx'
import ExcelUploadResult from './ExcelUploadResult.vue'

const alignLeftList = [
  '송하인주소', 
  '수하인주소', 
  '수하인상세주소', 
  '배송메모',
  'URL',
  '상품명', 
]

export default {
  name: "Modal-parcel-excel-upload-registration-form",
  components:{
    ExcelUploadResult
  },
  emits:['close-modal'],
  computed: {
    visibleRows() {
      if (!this.previewRows) return []
      const base = this.previewRows.filter(r => !r._hidden)
      const rows = this.showErrorsOnly ? base.filter(r => !r._valid) : base
      return rows.slice(0, this.displayLimit)
    },
    remainingCount() {
      const base = this.previewRows.filter(r => !r._hidden)
      const rows = this.showErrorsOnly ? base.filter(r => !r._valid) : base
      return Math.max(0, rows.length - this.displayLimit)
    },
    tableColumns() {
      const headers = Array.isArray(this.previewHeaders) ? this.previewHeaders : []
      const cols = headers.map(h => {
        const label = String(h.label ?? '').trim()
        return {
          name: String(h.name ?? ''),
          label,
          field: String(h.name ?? ''),
          align: alignLeftList.includes(label) ? 'left' : 'center',
          // align: 'center',
          sortable: false,
          headerStyle: 'min-width:160px; max-width:320px; text-align:center;',
          style: 'min-width:160px; max-width:320px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;'
        }
      })
      // prepend row number column
      cols.unshift({
        name: '_rowIndex',
        label: '행 번호',
        field: '_rowIndex',
        align: 'center',
        sortable: false,
        headerStyle: 'min-width:70px; max-width:70px; text-align:center;',
        style: 'min-width:70px; max-width:70px; text-align:center;'
      })
      return cols
    }
  },
  data() {
    return {
      step: 1, // Stepper 현재 단계
      file  : null,
      // resultShow  : false,
      fullDownloadUrl : "",
      successCount: 0,
      failedCount : 0,
      successRowCount: 0,
      failedRowCount : 0,
      successOrderCount: 0,
      successItemRowCount: 0,
      failedRows  : [],

      // preview state
      previewHeaders: [],
      previewRows: [],
      displayLimit: 200,
      showErrorsOnly: false,
      jumpRow: null,

      // cell editor state
      editDialog: false,
      editRow: null,
      editField: null,
      editValue: null,
    }
  },
  methods : {
    /**
     * Title        : 이전 단계로 이동
     * Description  : Stepper 컴포넌트의 이전 단계로 이동합니다.
     */
    movePrevStep() {
      if (this.step > 1) {
        this.$refs.stepper.previous()
      }
    },
    // 파일선택 핸들러: 항상 배열(또는 FileList)을 기대
    fileSelected(files) {
      // q-uploader의 @added는 배열을 넘깁니다.
      this.file = Array.isArray(files) ? files[0] : files // Changed to files[0] for single file
      this.$log.debug("업로드를 위해 선택된 파일 => ", this.file)
    },

    // 안전하게 선택된 실제 File 반환
    getSelectedFile() {
      return this.file; // Simplified as file is now singular
    },

    /**
     * Title        : 엑셀을 읽어 프리뷰용 데이터로 변환
     * Description  : 파일 객체를 받아 SheetJS로 파싱 후
     *                headers, rows 배열(행별 _rowIndex/_errors/_valid 포함)을 반환
     */
    async parseExcelToPreview(file) {
      if (!file) {
        throw new Error('No file provided')
      }

      // Read file as ArrayBuffer
      const buffer = await file.arrayBuffer()

      // Parse with SheetJS
      const wb = XLSX.read(buffer, { type: 'array' })
      if (!wb.SheetNames || wb.SheetNames.length === 0) throw new Error('엑셀 시트가 없습니다')
      
      const sheetName = wb.SheetNames[0]
      const ws = wb.Sheets[sheetName]

      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })

      // Use row 3 (index 2) as header labels;
      const headerRow = raw[2] || []
      
      // normalize header labels and produce unique field names
      const headerObjs = []
      const nameCount = {}

      headerRow.forEach((lbl, idx) => {
        const label = String(lbl ?? '').trim() || `Column ${idx + 1}`
        
        // base name: slugified label (fallback to col index)
        let base = label.replace(/\s+/g, '_').replace(/[^\w]/g, '') || `col_${idx+1}`

        this.$log.debug(`Header processing: label='${label}' => base='${base}'`)

        // ensure uniqueness
        if (nameCount[base] == null) {
          nameCount[base] = 1
        } else {
          nameCount[base] += 1
        }
        const name = nameCount[base] === 1 ? base : `${base}_${nameCount[base]}`

        // this.$log.debug(`Header : name='${name}'`)

        headerObjs.push({ name, label })
      })

      this.$log.debug(`[ 엑셀을 읽어 프리뷰용 데이터로 변환 ] headerObjs ='${headerObjs.length}'`, headerObjs)

      // data rows start from index 2 (Excel row 3); skip only row 0
      const dataRows = raw.slice(2)

      const rows = dataRows.map((r, idx) => {
        const obj = {}
        headerObjs.forEach((h, i) => { obj[h.name] = r[i] })
        obj._rowIndex = idx + 3 // 실제 엑셀 행 번호 (header rows counted)
        obj._errors = {}
        obj._valid = true
        return obj
      })

      // 첫 번째 데이터 행(엑셀 3행)은 업로드에는 포함하되 미리보기에서는 숨김
      if (rows.length > 0) {
        rows[0]._hidden = true
      }

      // 기본 1차 검증(예시) — 필요시 확장
      /*
      rows.forEach(row => {
        row._errors = this.validateRow(row, headerObjs)
        row._valid = Object.keys(row._errors).length === 0
      })
      */

      return { headers: headerObjs, rows }
    },

    /**
     * Title : 행 단위 기본 검증(샘플)
     * Description : required / 숫자 체크 등 간단한 룰
     *
     * Note: headers is now array of { name, label }
     * TODO: 실제 검증 규칙에 맞게 수정 필요
     */
    validateRow(row, headers) {
      const errors = {}

      // helper: find header by label
      const findHeader = (label) => headers.find(h => String(h.label).trim() === String(label).trim())

      // 예: '수량' 컬럼이 숫자여야 할 경우
      const qtyHeader = findHeader('수량')
      if (qtyHeader) {
        const val = String(row[qtyHeader.name] ?? '').trim()
        if (val === '') {
          errors[qtyHeader.name] = '필수 항목입니다'
        } else if (isNaN(Number(val))) {
          errors[qtyHeader.name] = '숫자여야 합니다'
        }
      }

      // 추가 검증 규칙은 여기 추가 (label 기준으로 header를 찾아 사용)
      return errors
    },

    /**
     * Title        : 엑셀 파일 파싱 및 미리보기
     * Description  : 선택된 엑셀 파일이 없으면 경고하고, 있으면 parseExcelToPreview로 헤더/행을 파싱해 previewHeaders/previewRows에 세팅 후 스텝퍼를 검증 단계로 이동시킵니다.
     */
    async regiByExcel(){
      const file = this.getSelectedFile()
      // 안전 검사
      if (!file) {
        this.$q.notify({ type: 'warning', message: '파일을 선택하세요.' })
        return
      }

      this.$log.debug("[ 엑셀 파일 파싱 및 미리보기 / this.file ] => ", file)
      
      try {
        const { headers, rows } = await this.parseExcelToPreview(file)
        this.previewHeaders = headers
        this.previewRows = rows
        // this.previewShow = true // Removed: now handled by stepper
        this.$log.debug('[ 엑셀 파일 파싱 및 미리보기 / Excel parsed for preview ] ', { headers, rowsCount: rows.length })
        this.$refs.stepper.next() // Move to next step (Preview & Validation)
      } catch (err) {
        this.$log.debug('엑셀 파싱 실패', err)
        this.$q.notify({ type: 'negative', message: '엑셀 파일을 읽는 중 오류가 발생했습니다.' })
      }
    },

    /**
     * Title        : 수정된 데이터 업로드
     * Description  : 미리보기에서 수정된 데이터를 기반으로 새로운 Excel 파일을 생성하여 서버로 업로드합니다.
     */
    async commitExcelUpload() {
      // 1. 데이터를 기반으로 새로운 Excel 파일 생성
      const headerLabels = this.previewHeaders.map(h => h.label);
      const headerFields = this.previewHeaders.map(h => h.name);
      
      // 미리보기에서 숨겼던 행(_hidden)은 업로드용 데이터에서도 제외
      const data = this.previewRows
        .filter(row => !row._hidden)
        .map(row => headerFields.map(field => row[field]));

      const sheetData = [
        headerLabels, // Row 1 (헤더: raw[1])
        ...data // Row 2 onwards (숨긴 첫 데이터 행 포함)
      ];

      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], {type: "application/octet-stream"});
      const baseName = this.file?.name ? this.file.name.replace(/\.xlsx?$/i, '') : 'modified_upload';
      const newFile = new File([blob], `${baseName}_modified.xlsx`, { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

      // const newFile = new File([blob], "modified_upload.xlsx", {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});

      // 2. 생성된 파일을 FormData에 담아 전송
      const payload = new FormData()
      payload.append("importExcel", newFile)
      for (const [k, v] of payload.entries()) {
        this.$log.debug('[ 수정된 엑셀 업로드 / Form data ] = ', k, v?.name || v, v?.size);
      }

      const config = {
        method  : "POST",
        url     : "/api/parcel/importExcel",
        formData: payload,
        name    : "[ AXIOS / 수정된 엑셀로 화물 업로드 ]",
        headers : "multipart"
      }

      try {
        const res = await this.$http(config)
        this.$log.debug(config.name + " 실행됨", res)

        if (res.status === 200) {
          const {
            successCount,
            failedCount,
            successRowCount,
            failedRowCount,
            successOrderCount,
            successItemRowCount,
            failedRows,
            failedExcelDownloadUrl
          } = res.data;

          this.successCount   = successCount
          this.failedCount    = failedCount
          this.successRowCount = successRowCount ?? successCount
          this.failedRowCount  = failedRowCount ?? failedCount
          this.successOrderCount = successOrderCount || 0
          this.successItemRowCount = successItemRowCount || 0
          this.failedRows     = failedRows
          this.fullDownloadUrl = failedExcelDownloadUrl
          // this.resultShow     = true // Show result dialog
          this.$refs.stepper.next() // Move to final step (Upload Complete)
        } else {
          this.$q.notify({
            type: 'negative',
            message: res.info?.reason || '서버 응답 오류',
            position: 'top',
            timeout: 2000
          })
        }
      } catch (err) {
        this.$log.debug(config.name + " 실패함 catch", err)
        this.$q.notify({
          type: 'negative',
          message: config.name + " 중 오류가 발생했습니다.",
          position: 'top',
          timeout: 2000
        })
      }
    },

    /**
     * Title        : 셀 편집기 열기
     * Description  : 테이블 셀 클릭 시 편집 다이얼로그를 엽니다.
     */
    openCellEditor(row, field) {
      this.editRow = row
      this.editField = field
      this.editValue = row[field]
      this.editDialog = true
    },
    /**
     * Title        : 셀 편집 내용 저장
     * Description  : 셀 편집 다이얼로그에서 변경된 내용을 저장하고 해당 행을 재검증합니다.
     */
    saveCellEdit() {
      if (!this.editRow || !this.editField) { this.editDialog = false; return }
      this.editRow[this.editField] = this.editValue
      this.editRow._errors = this.validateRow(this.editRow, this.previewHeaders)
      this.editRow._valid = Object.keys(this.editRow._errors).length === 0
      this.editDialog = false
      this.editRow = null; this.editField = null; this.editValue = null
    },
    /**
     * Title        : 셀 편집 취소
     * Description  : 셀 편집 다이얼로그를 닫고 상태를 초기화합니다.
     */
    cancelCellEdit() {
      this.editDialog = false
      this.editRow = null; this.editField = null; this.editValue = null
    },
    /**
     * Title        : 특정 행으로 스크롤 이동
     * Description  : 미리보기 테이블에서 지정된 행 번호로 스크롤을 이동하고, 필요한 경우 표시 제한을 확장합니다.
     */
    jumpToRow(rowNumber) {
      if (!rowNumber || !this.previewRows || this.previewRows.length === 0) return
      const n = Number(rowNumber)
      if (isNaN(n) || n < 1) return

      // previewRows는 0-based index; ensure displayLimit covers target
      const idx = n - 1
      if (this.displayLimit <= idx) {
        // 표시 범위 확장 (여유행 추가)
        this.displayLimit = idx + 50
      }

      this.$nextTick(() => {
        // q-table의 스크롤 컨테이너 찾아 스크롤 위치 설정
        const tableEl = this.$refs.previewTable?.$el?.querySelector('.q-table__middle') ||
                        this.$refs.previewTable?.$el?.querySelector('.q-table__container')
        if (tableEl) {
          const rowHeight = 48 // q-table 행 높이(레이아웃에 맞게 조정)
          // visibleRows의 시작 index (offset) — visibleRows = previewRows.slice(0, displayLimit) 또는 필터링 시 다름
          // 간단한 계산: 원하는 인덱스로 직접 스크롤
          tableEl.scrollTop = idx * rowHeight
        }
      })
    },
    /**
    * Title        : 상태 초기화
    * Description  : 모달이 닫히거나 Stepper가 첫 단계로 돌아갈 때 모든 상태를 초기값으로 리셋합니다
    */
    reset() {
      this.step = 1;
      this.file = null;
      this.previewHeaders = [];
      this.previewRows = [];
      this.successCount = 0;
      this.failedCount = 0;
      this.successRowCount = 0;
      this.failedRowCount = 0;
      this.successOrderCount = 0;
      this.successItemRowCount = 0;
      this.failedRows = [];
      this.fullDownloadUrl = "";
      this.displayLimit = 200;
      this.showErrorsOnly = false;
      this.jumpRow = null;
    },
   
    /**
    * Title        : 모달 닫기
    * Description  : 상태를 초기화하고 부모 컴포넌트에 모달 닫기 이벤트를 전달합니다.
    */
    close() {
      this.reset();
      this.$emit('close-modal');
    },

    /**
     * Title        : 실패 항목 엑셀 다운로드
     * Description  : 실패 항목이 있을 경우, 서버에서 제공하는 다운로드 URL을 통해 엑셀 파일을 다운로드합니다.
     */
    downloadFile() {
      const link = document.createElement('a')
      link.href = this.fullDownloadUrl
      link.setAttribute('download', '') // 파일명 지정하려면 download="파일명.xlsx"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  },
}
</script>

<template>
  <q-card class="card-size">
    <q-toolbar>
      <q-toolbar-title>
        <div class="text-h6">엑셀로 대량등록</div>
      </q-toolbar-title>
      <q-btn flat v-close-popup round dense icon="close" @click="close" />
    </q-toolbar>

    <q-stepper
      v-model="step"
      ref="stepper"
      color="primary"
      animated
      flat
    >
      <q-step
        :name="1"
        title="파일 선택"
        icon="file_upload"
        :done="step > 1"
      >
        <div class="q-pa-md">
          <q-uploader
            url=''
            @added="fileSelected"
            @removed="file = null"
            fieldName='importExcel'
            method="POST"
            accept=".xls,.xlsx"
            label="엑셀파일을 업로드해 주세요."
            class="full-width q-mb-md"
            hide-upload-btn
            square
            flat
            bordered
          />
        </div>
        
        <div class="q-px-md q-pb-md">
          <div class="comp-box">
            <p class="q-my-sm">
              1. 위 영역에 엑셀파일을 Drag & Drop을 합니다.<br>
              2. 하단의 '데이터 검증 및 미리보기' 버튼을 클릭해주세요.<br>
              3. 검증된 데이터를 확인하고 수정할 수 있습니다.<br>
              4. '데이터 등록' 버튼을 누르면 등록이 완료됩니다.<br>
            </p>
          </div>
        </div>
      </q-step>

      <q-step
        :name="2"
        title="미리보기 및 검증"
        icon="visibility"
        class="q-stepper-excel-upload-list"
        :done="step > 2"
      >

        <!-- Preview toolbar -->
        <div class="q-mb-sm row items-center preview-toolbar">
          <q-toggle v-model="showErrorsOnly" label="오류 행만 보기" dense />
          <q-btn flat size="sm" label="더 보기" v-if="remainingCount>0" @click="displayLimit += 200" />
          <q-btn flat size="sm" label="모두 보기" v-if="previewRows.length>displayLimit" @click="displayLimit = previewRows.length" />
          <div class="row items-center q-ml-md">
            <q-input dense v-model.number="jumpRow" type="number" placeholder="행번호" style="width:100px" />
            <q-btn flat size="sm" label="이동" @click="jumpToRow(jumpRow)" />
          </div>
          <div class="text-caption q-ml-sm">총 {{ previewRows.length }} 행 / 표시 {{ visibleRows.length }}</div>
        </div>
        <!--// Preview toolbar -->
        <q-table
          ref="previewTable"
          :rows="visibleRows"
          :columns="tableColumns"
          row-key="_rowIndex"
          flat
          dense
          virtual-scroll
          table-header-class="sticky-header"
          :rows-per-page-options="[]"
          :pagination="{ rowsPerPage: 0 }"
          class="my-sticky-table"
        >
          <template v-slot:body-cell="props">
            <q-td :props="props" :style="props.col.style">
              <div @click="openCellEditor(props.row, props.col.name)" style="cursor: pointer;">
                <span v-if="props.row._errors && props.row._errors[props.col.name]" class="text-negative">
                  {{ props.row[props.col.name] }}
                </span>
                <span v-else>{{ props.row[props.col.name] }}</span>
                <q-tooltip :delay="700" anchor="bottom middle" self="top middle">
                  {{ props.row[props.col.name] }}
                </q-tooltip>
              </div>
              <div v-if="props.row._errors && props.row._errors[props.col.name]" class="text-negative text-caption">
                {{ props.row._errors[props.col.name] }}
              </div>
            </q-td>
          </template>
        </q-table>
      </q-step>

      <q-step
        :name="3"
        title="업로드 완료"
        icon="cloud_done"
      >
        <div class="text-center q-pa-xl">
          <q-icon name="cloud_done" color="primary" size="xl" />
          <div class="text-h6 q-mt-md">업로드를 완료했습니다.</div>
          <ExcelUploadResult
            :success-count="successCount"
            :failed-count="failedCount"
            :success-row-count="successRowCount"
            :failed-row-count="failedRowCount"
            :success-order-count="successOrderCount"
            :success-item-row-count="successItemRowCount"
            :failed-rows="failedRows"
          />
        </div>
      </q-step>

      <template v-slot:navigation>
        <q-stepper-navigation class="q-pa-md">
          <q-toolbar>
            <q-btn
              v-if="step === 2"
              flat
              color="primary"
              @click="movePrevStep"
              label="다시 선택"
              class="q-mr-sm"
            />
            <q-space />
            <q-btn
              v-if="step === 1"
              @click="regiByExcel"
              color="primary"
              label="데이터 검증 및 미리보기"
            />
            <q-btn
              v-if="step === 2"
              @click="commitExcelUpload"
              color="primary"
              label="데이터 등록"
            />

            <q-btn
              v-show="failedCount > 0"
              label="실패 항목 엑셀 다운로드"
              color="primary"
              @click="downloadFile">
            </q-btn>
            
            <q-btn
              v-if="step === 3"
              color="primary"
              label="팝업 닫기"
              @click="close"
            />
          </q-toolbar>
        </q-stepper-navigation>
      </template>
    </q-stepper>
  </q-card>

  <Teleport to="body">
    <!-- Cell edit dialog -->
    <q-dialog v-model="editDialog" persistent>
      <q-card style="min-width:320px">
        <q-card-section>
          <div class="text-subtitle1 q-mb-sm">편집: {{ editField }}</div>
          <q-input v-model="editValue" autofocus @keyup.enter="saveCellEdit" />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="취소" @click="cancelCellEdit" />
          <q-btn color="primary" label="저장" @click="saveCellEdit" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Result Dialog
    <q-dialog 
      v-model="resultShow"
      persistent
      @hide="step = 1; file = null;"
    >
      <ExcelUploadResult
        :full-download-url="fullDownloadUrl"
        :success-count="successCount"
        :failed-count="failedCount"
        :failed-rows="failedRows"
        @close-modal="resultShow = false; this.$emit('close-modal')"
      />
    </q-dialog>
     -->
  </Teleport>
</template>

<style scoped lang="scss">
.card-size {
  width: 900px; 
  max-width: 90vw;
  overflow: hidden;
}

.q-toolbar {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 100;
}

.q-stepper {
  height: calc(80vh);
  display: flex;
  flex-direction: column;
}

.preview-toolbar {
  position: sticky;
  top: 0;
  z-index: 15;
  background: #fff;
  padding: 8px 0;
}

.my-sticky-table {
  height: calc(80vh - 250px);
  overflow: auto;

  :deep(.q-table__card) {
    background: transparent;
  }
  :deep(.q-table thead tr) {
    position: sticky !important;
    top: 0;
    z-index: 10;
    background: #f5f5f5;
  }
  :deep(.q-table th) {
    background: #f5f5f5;
    font-weight: bold;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  :deep(.q-table td) {
    background: #fff;
    text-overflow: ellipsis;
  }
}

</style>
